import { useCallback, useEffect, useMemo, useState } from 'react';
import { useVault } from '../lib/vault';
import { api } from '../lib/api';
import { CYCLE_LABEL, annualMoney, formatMoney, periodMoney, sumMoney } from '@shared/money';
import { plural } from '@shared/text';
import { FIXED_STATUS_LABEL, PAYMENT_LABEL, payrollTotal, type FixedCost } from '@shared/types';
import { daysUntil } from '@shared/money';
import { FixedCostEditor } from '../components/FixedCostEditor';
import { ItemIcon } from '../components/ItemIcon';
import { Confirm, ErrorNote, useAction } from '../components/ui';

/** Die Form, in der Prognose und Kostenrechnung einen Posten verstehen. */
function alsVertrag(cost: FixedCost): Parameters<typeof annualMoney>[0] {
  return {
    priceCents: cost.amountCents,
    cycle: cost.cycle,
    currency: cost.currency,
    status: cost.status === 'active' ? 'active' : 'cancelled',
    priceIsPerSeat: false,
    seats: null,
    hasCost: true,
    priceIsNet: cost.priceIsNet,
    vatRatePercent: cost.vatRatePercent,
  } as never;
}

export function FixedCostsPage(): JSX.Element {
  const { users } = useVault();
  const [costs, setCosts] = useState<FixedCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState<{ cost: FixedCost | null } | null>(null);
  const [loeschen, setLoeschen] = useState<FixedCost | null>(null);
  const [query, setQuery] = useState('');
  const { error, setError } = useAction();

  const laden = useCallback(async () => {
    setLoading(true);
    try {
      setCosts(await api.fixedCosts());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const auswertung = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sichtbar = costs.filter(
      (c) => !q || `${c.title} ${c.category} ${c.recipient}`.toLowerCase().includes(q),
    );
    const laufend = sichtbar.filter((c) => c.status === 'active');

    const monatlich = sumMoney(
      laufend.filter((c) => c.cycle === 'monthly').map((c) => periodMoney(alsVertrag(c))),
    );
    const jahr = sumMoney(laufend.map((c) => annualMoney(alsVertrag(c))));

    const nachKategorie = new Map<string, { name: string; cents: number; anzahl: number }>();
    for (const c of laufend) {
      const name = c.category.trim() || 'Ohne Kategorie';
      const b = nachKategorie.get(name) ?? { name, cents: 0, anzahl: 0 };
      b.cents += annualMoney(alsVertrag(c)).grossCents;
      b.anzahl += 1;
      nachKategorie.set(name, b);
    }

    const fristen = sichtbar
      .filter((c) => c.status === 'active')
      .flatMap((c) => {
        const tage = daysUntil(c.cancelBeforeDate);
        return tage !== null && tage <= 90 ? [{ cost: c, tage }] : [];
      })
      .sort((a, b) => a.tage - b.tage);

    return {
      sichtbar: [...sichtbar].sort(
        (a, b) => annualMoney(alsVertrag(b)).grossCents - annualMoney(alsVertrag(a)).grossCents,
      ),
      laufend,
      monatlich,
      jahr,
      kategorien: [...nachKategorie.values()].sort((a, b) => b.cents - a.cents),
      fristen,
      schwankend: laufend.filter((c) => c.isVariable).length,
      personal: laufend
        .flatMap((cost) => cost.positions.map((position) => ({ cost, position })))
        .sort(
          (a, b) =>
            b.position.grossCents +
            b.position.employerCostCents -
            (a.position.grossCents + a.position.employerCostCents),
        ),
      personalSumme: laufend.reduce((s, c) => s + payrollTotal(c.positions), 0),
    };
  }, [costs, query]);

  const nameOf = (uid: string | null): string => users.find((u) => u.uid === uid)?.name ?? '–';
  const max = Math.max(1, ...auswertung.kategorien.map((k) => k.cents));

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Sonstige Kosten</h1>
        </div>
        <div className="row-tight">
          <input
            style={{ maxWidth: 200 }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suchen …"
            aria-label="Suchen"
          />
          <button className="btn-primary" onClick={() => setEditor({ cost: null })}>
            Neuer Posten
          </button>
        </div>
      </div>

      <ErrorNote error={error} />

      <div className="grid grid-kpi">
        <div className="card">
          <div className="kpi-label">Monatlich</div>
          <div className="kpi-value">{formatMoney(auswertung.monatlich.grossCents)}</div>
          <div className="kpi-hint">nur monatlich abgebuchte Posten</div>
        </div>
        <div className="card">
          <div className="kpi-label">Aufs Jahr</div>
          <div className="kpi-value">{formatMoney(auswertung.jahr.grossCents)}</div>
          <div className="kpi-hint">alle Rhythmen zusammengerechnet</div>
        </div>
        <div className="card">
          <div className="kpi-label">Posten</div>
          <div className="kpi-value">{auswertung.laufend.length}</div>
          <div className="kpi-hint">
            {auswertung.schwankend > 0
              ? `${auswertung.schwankend} davon mit schwankendem Betrag`
              : 'alle mit festem Betrag'}
          </div>
        </div>
      </div>

      {auswertung.fristen.length > 0 ? (
        <div className="note note-warn" style={{ marginTop: 14 }}>
          <strong>
            Bei {plural(auswertung.fristen.length, 'Posten läuft', 'Posten laufen')} in den
            nächsten 90 Tagen eine Kündigungsfrist ab.
          </strong>
          <div className="small" style={{ marginTop: 6 }}>
            {auswertung.fristen
              .map((f) => `${f.cost.title}: ${f.tage < 0 ? 'verstrichen' : `noch ${f.tage} Tage`}`)
              .join(' · ')}
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="empty">Wird geladen …</div>
      ) : costs.length === 0 ? (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="empty">
            Noch nichts erfasst.
            <div className="small" style={{ marginTop: 8, maxWidth: '46ch', margin: '8px auto 0' }}>
              Lohn, Gewerbesteuer, Betriebshaftpflicht und Miete sind ein guter Anfang – das sind
              meist die vier größten Posten und genau die, die man selten im Kopf hat.
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-2" style={{ marginTop: 14 }}>
            <div className="card">
              <div className="card-title">
                <h2>Nach Kategorie</h2>
                <span className="small muted">brutto aufs Jahr</span>
              </div>
              <div className="stack">
                {auswertung.kategorien.map((k) => (
                  <div key={k.name}>
                    <div className="row small" style={{ marginBottom: 5 }}>
                      <strong>{k.name}</strong>
                      <span className="muted">({k.anzahl})</span>
                      <span className="spacer" />
                      <span className="mono">{formatMoney(k.cents)}</span>
                    </div>
                    <div className="bar-track">
                      <div className="bar-fill" style={{ width: `${Math.max(2, (k.cents / max) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-title">
                <h2>Größte Posten</h2>
                <span className="small muted">brutto aufs Jahr</span>
              </div>
              <table>
                <tbody>
                  {auswertung.sichtbar.slice(0, 8).map((c) => (
                    <tr key={c.id}>
                      <td>
                        <strong>{c.title}</strong>
                        <div className="small muted">{c.recipient || c.category}</div>
                      </td>
                      <td className="num mono">
                        {formatMoney(annualMoney(alsVertrag(c)).grossCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {auswertung.personal.length > 0 ? (
            <div className="card" style={{ marginTop: 14 }}>
              <div className="card-title">
                <h2>Personal im Einzelnen</h2>
                <span className="small muted">
                  Brutto plus Arbeitgeberanteil – was tatsächlich abgeht
                </span>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Person</th>
                    <th className="num col-optional">Brutto</th>
                    <th className="num col-optional">Arbeitgeber</th>
                    <th className="num">Kostet</th>
                  </tr>
                </thead>
                <tbody>
                  {auswertung.personal.map(({ cost, position }) => (
                    <tr key={`${cost.id}-${position.id}`}>
                      <td>
                        <strong>{position.name}</strong>
                        <div className="small muted">
                          {[position.role, position.note].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td className="num mono col-optional">{formatMoney(position.grossCents)}</td>
                      <td className="num mono muted col-optional">
                        {formatMoney(position.employerCostCents)}
                      </td>
                      <td className="num mono">
                        <strong>
                          {formatMoney(position.grossCents + position.employerCostCents)}
                        </strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="small muted" style={{ marginTop: 10 }}>
                Der Arbeitgeberanteil ist Sozialversicherung, Umlagen und Berufsgenossenschaft –
                er kommt zum Brutto hinzu. Lohnsteuer und Arbeitnehmeranteil stecken schon im
                Brutto und dürfen nicht zusätzlich erfasst werden.
              </p>
            </div>
          ) : null}

          <div className="card" style={{ marginTop: 14 }}>
            <div className="card-title">
              <h2>Alle Posten</h2>
              <span className="small muted">{plural(auswertung.sichtbar.length, 'Posten', 'Posten')}</span>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Posten</th>
                  <th className="col-optional">Rhythmus</th>
                  <th className="col-optional">Zahlung</th>
                  <th className="num">aufs Jahr</th>
                  <th className="col-optional" />
                </tr>
              </thead>
              <tbody>
                {auswertung.sichtbar.map((c) => (
                  <tr key={c.id} className="clickable" onClick={() => setEditor({ cost: c })}>
                    <td>
                      <div className="row-tight">
                        <ItemIcon item={{ ...c, title: c.title }} size={22} />
                        <div>
                          <div className="row-tight">
                            <strong>{c.title}</strong>
                            {c.status !== 'active' ? (
                              <span className="badge">{FIXED_STATUS_LABEL[c.status]}</span>
                            ) : null}
                            {c.isVariable ? <span className="badge badge-warn">schwankt</span> : null}
                          </div>
                          <div className="small muted">
                            {[
                              c.category,
                              c.recipient,
                              c.positions.length > 0
                                ? plural(c.positions.length, 'Person', 'Personen')
                                : '',
                              c.reference,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="small muted col-optional">
                      {formatMoney(c.amountCents, c.currency)} {CYCLE_LABEL[c.cycle]}
                      {c.billingDay ? <div className="small muted">am {c.billingDay}.</div> : null}
                      {c.dueDate ? <div className="small muted">fällig {c.dueDate}</div> : null}
                    </td>
                    <td className="small muted col-optional">
                      {PAYMENT_LABEL[c.paymentMethod]}
                      <div className="small muted">{nameOf(c.ownerUid)}</div>
                    </td>
                    <td className="num mono">{formatMoney(annualMoney(alsVertrag(c)).grossCents)}</td>
                    <td className="num col-optional">
                      <button
                        className="btn-quiet btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLoeschen(c);
                        }}
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editor ? (
        <FixedCostEditor cost={editor.cost} onClose={() => setEditor(null)} onSaved={laden} />
      ) : null}

      {loeschen ? (
        <Confirm
          danger
          title={`„${loeschen.title}" löschen?`}
          confirmLabel="Löschen"
          message={
            <p>
              Der Posten verschwindet aus der Übersicht und der Prognose. Im Jahresrückblick
              bleibt die Änderung als Ersparnis erhalten.
            </p>
          }
          onConfirm={async () => {
            await api.deleteFixedCost(loeschen.id);
            await laden();
          }}
          onClose={() => setLoeschen(null)}
        />
      ) : null}
    </>
  );
}
